import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Avatar,
  TextField,
  Button,
  Chip,
  Divider,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import SecurityIcon from "@mui/icons-material/Security";
import EmailIcon from "@mui/icons-material/Email";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import PeopleIcon from "@mui/icons-material/People";
import LockResetIcon from "@mui/icons-material/LockReset";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../firebase/authContext";
import { getUserNotes } from "../../firebase/notesService";
import { getReadChapterCount } from "../../bible/readingHistoryStorage";
import { d1GetFriends } from "../../services/d1Client";
import { MOBILE_BOTTOM_NAV_HEIGHT } from "../AppHeader";

const profileDraftKey = (uid) => `bible_profile_draft:${uid}`;

const readProfileDraft = (uid) => {
  if (!uid) return null;
  try {
    const raw = sessionStorage.getItem(profileDraftKey(uid));
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeProfileDraft = (uid, draft) => {
  if (!uid) return;
  try {
    sessionStorage.setItem(profileDraftKey(uid), JSON.stringify(draft));
  } catch (e) {}
};

const clearProfileDraft = (uid) => {
  if (!uid) return;
  try {
    sessionStorage.removeItem(profileDraftKey(uid));
  } catch (e) {}
};

const profileBelongsToUser = (profile, uid) => !profile?.uid || profile.uid === uid;

const resolveProfileForm = (user, profile) => {
  if (!user) {
    return { displayName: "", photoURL: "", bio: "", dirty: false };
  }

  const draft = readProfileDraft(user.uid);
  const profileIsCurrent = profileBelongsToUser(profile, user.uid);
  return {
    displayName:
      draft?.displayName ??
      (profileIsCurrent ? profile?.displayName || user.displayName || "" : user.displayName || ""),
    photoURL:
      draft?.photoURL ??
      (profileIsCurrent ? profile?.photoURL || user.photoURL || "" : user.photoURL || ""),
    bio: draft?.bio ?? (profileIsCurrent ? profile?.bio || "" : ""),
    dirty: Boolean(draft),
  };
};

// Preset spiritual and peaceful avatars with gradient colors & SVG/emoji icons
const PRESET_AVATARS = [
  { id: "dove", label: "和平之鸽", emoji: "🕊️", bg: "#1976d2" },
  { id: "cross", label: "荣耀十架", emoji: "✝️", bg: "#9c27b0" },
  { id: "bible", label: "生命真道", emoji: "📖", bg: "#2e7d32" },
  { id: "olive", label: "信实橄榄", emoji: "🌿", bg: "#388e3c" },
  { id: "light", label: "真理之光", emoji: "🕯️", bg: "#ed6c02" },
  { id: "sunrise", label: "晨星初现", emoji: "🌅", bg: "#d32f2f" },
  { id: "mountain", label: "恩典之岭", emoji: "⛰️", bg: "#0288d1" },
  { id: "water", label: "活水江河", emoji: "💧", bg: "#0097a7" },
];

export default function ProfilePage() {
  const { currentUser, userProfile, isAdmin, updateUserProfile, resetPassword, loading } = useAuth();
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState("");
  const [photoURL, setPhotoURL] = useState("");
  const [bio, setBio] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");
  const [saveFeedback, setSaveFeedback] = useState("");

  // Statistics — seed read count from the same helper home / history use
  const [stats, setStats] = useState(() => ({
    readCount: getReadChapterCount(),
    notesCount: 0,
    friendsCount: 0,
  }));

  // Avatar Picker Modal
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [customUrlInput, setCustomUrlInput] = useState("");
  const fileInputRef = useRef(null);
  const formOwnerUidRef = useRef(null);
  const prevUidRef = useRef(undefined);
  const signedInUid = currentUser?.uid ?? null;

  useEffect(() => {
    const uidChanged = prevUidRef.current !== signedInUid;
    prevUidRef.current = signedInUid;

    if (uidChanged) {
      setToastMsg("");
      setSaveFeedback("");
      setSaving(false);
      setStats({
        readCount: getReadChapterCount(),
        notesCount: 0,
        friendsCount: 0,
      });
    }

    if (!currentUser) {
      if (uidChanged) {
        const empty = resolveProfileForm(null, null);
        setDisplayName(empty.displayName);
        setPhotoURL(empty.photoURL);
        setBio(empty.bio);
        setDirty(false);
        formOwnerUidRef.current = null;
      }
      return undefined;
    }

    // Always re-hydrate when the signed-in user changes, even if the previous
    // account left the form dirty. Otherwise account A's name/bio can be saved onto B.
    if (uidChanged || (!saving && !dirty)) {
      const next = resolveProfileForm(currentUser, userProfile);
      setDisplayName(next.displayName);
      setPhotoURL(next.photoURL);
      setBio(next.bio);
      setDirty(next.dirty);
      formOwnerUidRef.current = currentUser.uid;
    }

    const applyReadCount = () => {
      setStats((prev) => ({ ...prev, readCount: getReadChapterCount() }));
    };
    applyReadCount();
    window.addEventListener("bible_read_status_changed", applyReadCount);

    const requestedUid = currentUser.uid;
    Promise.all([
      Promise.resolve(getUserNotes(requestedUid)).catch(() => []),
      Promise.resolve(d1GetFriends()).catch(() => []),
    ]).then(([notes, friends]) => {
      if (formOwnerUidRef.current !== requestedUid) return;
      setStats((prev) => ({
        ...prev,
        notesCount: Array.isArray(notes) ? notes.length : 0,
        friendsCount: Array.isArray(friends) ? friends.length : 0,
      }));
    });

    return () => {
      window.removeEventListener("bible_read_status_changed", applyReadCount);
    };
  }, [currentUser, userProfile, saving, dirty, signedInUid]);

  if (loading || (currentUser && !userProfile)) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: "center" }}>
        <Box data-testid="profile-loading" aria-busy="true" aria-label="正在加载个人资料">
          <CircularProgress size={36} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            正在加载个人资料...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!currentUser) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: "center" }}>
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
            请先登录您的账号
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            登录后即可查看与修改您的个人资料、阅读记录与灵修笔记。
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/")}
            sx={{ borderRadius: 2, fontWeight: 700 }}
          >
            返回首页登录
          </Button>
        </Paper>
      </Container>
    );
  }

  // Handle local file upload
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setToastSeverity("error");
      setToastMsg("请选择有效的图片文件 (PNG, JPG, WEBP)");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setToastSeverity("error");
      setToastMsg("图片体积过大，请选择 2MB 以内的图片");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        // Resize image to max 200x200 canvas
        const canvas = document.createElement("canvas");
        const maxDim = 200;
        let width = img.width;
        let height = img.height;
        if (width > height) {
          if (width > maxDim) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          }
        } else {
          if (height > maxDim) {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        setPhotoURL(dataUrl);
        setAvatarModalOpen(false);
        setToastSeverity("success");
        setToastMsg("头像已选取，请点击页面下方「保存资料」完成更新！");
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  // Select Preset Avatar
  const handleSelectPreset = (preset) => {
    // Generate SVG Data URI for the emoji avatar
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100">
      <rect width="100" height="100" rx="50" fill="${preset.bg}"/>
      <text x="50" y="65" font-size="48" text-anchor="middle" dominant-baseline="central">${preset.emoji}</text>
    </svg>`;
    const dataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
    setPhotoURL(dataUrl);
    setAvatarModalOpen(false);
    setToastSeverity("success");
    setToastMsg(`已选择「${preset.label}」头像，请点击下方「保存资料」！`);
  };

  // Save Profile
  const handleSaveProfile = async (e) => {
    e?.preventDefault();
    if (!currentUser) return;
    if (
      (formOwnerUidRef.current && formOwnerUidRef.current !== currentUser.uid) ||
      (userProfile?.uid && userProfile.uid !== currentUser.uid)
    ) {
      setSaveFeedback("error");
      setToastSeverity("error");
      setToastMsg("账号已切换，请确认当前资料后再保存");
      return;
    }
    if (!displayName.trim()) {
      setSaveFeedback("error");
      setToastSeverity("error");
      setToastMsg("昵称不能为空，请输入有效的读者昵称");
      return;
    }

    setSaving(true);
    const nextDraft = {
      displayName: displayName.trim(),
      photoURL: photoURL.trim(),
      bio: bio.trim(),
    };
    writeProfileDraft(currentUser.uid, nextDraft);
    try {
      await updateUserProfile(nextDraft);
      clearProfileDraft(currentUser.uid);
      setDirty(false);
      setSaveFeedback("saved");
      setToastSeverity("success");
      setToastMsg("资料已保存");
    } catch (err) {
      setSaveFeedback("error");
      setToastSeverity("error");
      const detail = String(err?.message || "").trim();
      setToastMsg(
        detail && !/^D1 API Error/i.test(detail) && detail !== "Not signed in"
          ? `保存失败：${detail}`
          : "保存失败，请稍后重试"
      );
    } finally {
      setSaving(false);
    }
  };

  // Password reset email
  const handleResetPassword = async () => {
    if (!currentUser.email) return;
    try {
      await resetPassword(currentUser.email);
      setToastSeverity("success");
      setToastMsg(`重置密码邮件已发送至 ${currentUser.email}，请查收邮箱链接！`);
    } catch (err) {
      setToastSeverity("error");
      setToastMsg("发送重置邮件失败，请稍后重试");
    }
  };

  const initial = (displayName || currentUser.email?.split("@")[0] || "读")[0].toUpperCase();

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header Bar */}
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconButton onClick={() => navigate(-1)} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, fontSize: { xs: "1.15rem", sm: "1.5rem" } }}>
              个人资料与账号设置
            </Typography>
            <Typography variant="body2" color="text.secondary">
              管理您的读者名片、头像形象及账号安全
            </Typography>
          </Box>
        </Box>
      </Box>

      {(currentUser.isGuest || currentUser.uid === "local_guest_user") && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          访客模式下资料仅保存在本机，登录账号后即可同步到云端。
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left Column: Avatar & Summary */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {/* Avatar with edit overlay badge */}
            <Box sx={{ position: "relative", mb: 2 }}>
              <Avatar
                src={photoURL}
                alt={displayName}
                sx={{
                  width: 96,
                  height: 96,
                  fontSize: "2.2rem",
                  bgcolor: "primary.main",
                  fontWeight: 800,
                  boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
                }}
              >
                {initial}
              </Avatar>
              <Tooltip title="更换头像">
                <IconButton
                  onClick={() => setAvatarModalOpen(true)}
                  color="primary"
                  sx={{
                    position: "absolute",
                    bottom: -2,
                    right: -2,
                    bgcolor: "background.paper",
                    border: "2px solid",
                    borderColor: "primary.main",
                    p: 0.75,
                    "&:hover": { bgcolor: "primary.light", color: "#fff" },
                  }}
                >
                  <PhotoCameraIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
              {displayName || "读者"}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, mb: 1 }}>
              <EmailIcon sx={{ fontSize: 14, color: "text.secondary" }} />
              <Typography variant="caption" color="text.secondary" noWrap>
                {currentUser.email}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 1, justifyContent: "center", flexWrap: "wrap", mt: 0.5 }}>
              <Chip
                icon={<CheckCircleOutlineIcon sx={{ fontSize: "14px !important" }} />}
                label={currentUser.emailVerified ? "已验证邮箱" : "已注册读者"}
                size="small"
                color="success"
                variant="outlined"
                sx={{ height: 22, fontSize: "0.7rem", fontWeight: 700 }}
              />
              {isAdmin && (
                <Chip
                  icon={<SecurityIcon sx={{ fontSize: "14px !important" }} />}
                  label="管理员"
                  size="small"
                  color="secondary"
                  sx={{ height: 22, fontSize: "0.7rem", fontWeight: 700 }}
                />
              )}
            </Box>

            <Divider sx={{ my: 2.5, width: "100%" }} />

            {/* Quick Stats Grid */}
            <Grid container spacing={1.5} sx={{ width: "100%" }}>
              <Grid item xs={4}>
                <Box sx={{ p: 1, borderRadius: 2, bgcolor: (theme) => theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "#f8f9fa" }}>
                  <AutoStoriesIcon color="primary" fontSize="small" />
                  <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                    {stats.readCount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                    已读章数
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={4}>
                <Box sx={{ p: 1, borderRadius: 2, bgcolor: (theme) => theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "#f8f9fa" }}>
                  <NoteAltIcon color="secondary" fontSize="small" />
                  <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                    {stats.notesCount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                    灵修笔记
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={4}>
                <Box sx={{ p: 1, borderRadius: 2, bgcolor: (theme) => theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "#f8f9fa" }}>
                  <PeopleIcon color="info" fontSize="small" />
                  <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                    {stats.friendsCount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                    主内好友
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Right Column: Edit Profile Form & Account Security */}
        <Grid item xs={12} md={8}>
          <Paper
            component="form"
            onSubmit={handleSaveProfile}
            elevation={0}
            sx={{
              p: 3.5,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              mb: 3,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 2.5, display: "flex", alignItems: "center", gap: 1 }}>
              <EditIcon color="primary" fontSize="small" />
              修改基本资料
            </Typography>

            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, mb: 0.5, display: "block" }}>
                  读者昵称 / 显示名称
                </Typography>
                <TextField
                  id="profile-display-name"
                  name="displayName"
                  fullWidth
                  size="small"
                  value={displayName}
                  onChange={(e) => {
                    setDisplayName(e.target.value);
                    setDirty(true);
                    setSaveFeedback("");
                    writeProfileDraft(currentUser.uid, {
                      displayName: e.target.value,
                      photoURL,
                      bio,
                    });
                  }}
                  placeholder="请输入您的主内昵称或称呼"
                  helperText="此昵称将用于灵修笔记分享、好友互动及社区交流显示。"
                  inputProps={{ maxLength: 50, name: "displayName" }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, mb: 0.5, display: "block" }}>
                  灵修签名 / 属灵简介
                </Typography>
                <TextField
                  id="profile-bio"
                  name="bio"
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  maxRows={4}
                  value={bio}
                  onChange={(e) => {
                    setBio(e.target.value);
                    setDirty(true);
                    setSaveFeedback("");
                    writeProfileDraft(currentUser.uid, {
                      displayName,
                      photoURL,
                      bio: e.target.value,
                    });
                  }}
                  placeholder="用一两句话分享你的灵修心志，将显示在主内名片上"
                  helperText={`${bio.length}/200，留空则使用默认签名`}
                  inputProps={{ maxLength: 200, name: "bio" }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, mb: 0.5, display: "block" }}>
                  头像链接 / 头像图片
                </Typography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <TextField
                    id="profile-photo-url"
                    name="photoURL"
                    fullWidth
                    size="small"
                    value={photoURL.startsWith("data:") ? "" : photoURL}
                    onChange={(e) => setPhotoURL(e.target.value)}
                    inputProps={{ name: "photoURL" }}
                    placeholder={
                      photoURL.startsWith("data:")
                        ? "已使用预设或本地头像"
                        : "https://... 或点击右侧「选择头像」"
                    }
                    helperText="支持在线图片链接、预设属灵头像，或本地照片上传。"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => setAvatarModalOpen(true)}
                    startIcon={<PhotoCameraIcon />}
                    sx={{ borderRadius: 2, height: 40, whiteSpace: "nowrap", textTransform: "none" }}
                  >
                    更换头像
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12}>
                {saveFeedback === "saved" && (
                  <Alert
                    severity="success"
                    data-testid="profile-save-feedback"
                    role="status"
                    sx={{ mb: 1.5, borderRadius: 2 }}
                  >
                    已保存
                  </Alert>
                )}
                {saveFeedback === "error" && (
                  <Alert
                    severity="error"
                    data-testid="profile-save-feedback"
                    role="status"
                    sx={{ mb: 1.5, borderRadius: 2 }}
                  >
                    保存失败
                  </Alert>
                )}
                <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1.5, mt: 1 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={saving}
                    sx={{
                      borderRadius: 2.5,
                      px: 4,
                      py: 1,
                      fontWeight: 700,
                      textTransform: "none",
                      boxShadow: "0 4px 12px rgba(25, 118, 210, 0.25)",
                    }}
                  >
                    {saving ? <CircularProgress size={20} color="inherit" /> : "保存资料修改"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Account Security Card */}
          <Paper
            elevation={0}
            sx={{
              p: 3.5,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
              <SecurityIcon color="primary" fontSize="small" />
              账号与安全
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2, py: 1 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  登录密码管理
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  如需修改登录密码，系统将向您的注册邮箱发送密码重置安全链接。
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                startIcon={<LockResetIcon />}
                onClick={handleResetPassword}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
              >
                重置登录密码
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  注册时间：{userProfile?.createdAt ? new Date(userProfile.createdAt).toLocaleString() : "早期读者"}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  账号唯一标识 (UID)：<code>{currentUser.uid}</code>
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Avatar Picker Modal */}
      <Dialog
        open={avatarModalOpen}
        onClose={() => setAvatarModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, borderBottom: "1px solid", borderColor: "divider" }}>
          选择或上传个人头像
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          {/* Preset Spiritual Avatars */}
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5 }}>
            精选属灵与安息预设头像
          </Typography>
          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            {PRESET_AVATARS.map((preset) => (
              <Grid item xs={3} sm={3} key={preset.id}>
                <Box
                  onClick={() => handleSelectPreset(preset)}
                  sx={{
                    p: 1.5,
                    borderRadius: 2.5,
                    border: "1px solid",
                    borderColor: "divider",
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      borderColor: "primary.main",
                      bgcolor: "rgba(25, 118, 210, 0.05)",
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      mx: "auto",
                      mb: 0.5,
                      bgcolor: preset.bg,
                      fontSize: "1.5rem",
                    }}
                  >
                    {preset.emoji}
                  </Avatar>
                  <Typography variant="caption" sx={{ fontWeight: 700 }} noWrap>
                    {preset.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Local Upload */}
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
            从本地照片上传
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
            支持 JPG、PNG、WEBP 格式，系统将自动进行适屏压缩优化。
          </Typography>
          <input
            id="profile-avatar-file"
            name="avatarFile"
            type="file"
            accept="image/*"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileUpload}
          />
          <Button
            variant="outlined"
            fullWidth
            startIcon={<PhotoCameraIcon />}
            onClick={() => fileInputRef.current?.click()}
            sx={{ borderRadius: 2, py: 1, textTransform: "none", fontWeight: 700, mb: 3 }}
          >
            选择本地图片文件上传
          </Button>

          <Divider sx={{ my: 2 }} />

          {/* Custom URL */}
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
            输入在线图片链接
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              id="profile-avatar-url"
              name="avatarUrl"
              size="small"
              fullWidth
              placeholder="https://example.com/avatar.jpg"
              value={customUrlInput}
              onChange={(e) => setCustomUrlInput(e.target.value)}
              inputProps={{ name: "avatarUrl" }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <Button
              variant="contained"
              disabled={!customUrlInput.trim()}
              onClick={() => {
                setPhotoURL(customUrlInput.trim());
                setAvatarModalOpen(false);
              }}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
            >
              采用链接
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAvatarModalOpen(false)} sx={{ borderRadius: 2 }}>
            关闭
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar feedback */}
      <Snackbar
        open={Boolean(toastMsg)}
        autoHideDuration={4500}
        onClose={(_event, reason) => {
          if (reason === "clickaway") return;
          setToastMsg("");
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
          bottom: {
            xs: `calc(${MOBILE_BOTTOM_NAV_HEIGHT}px + 16px + env(safe-area-inset-bottom, 0px))`,
            sm: 24,
          },
        }}
      >
        <Alert
          severity={toastSeverity}
          variant="filled"
          data-testid="profile-save-toast"
          onClose={() => setToastMsg("")}
          sx={{ borderRadius: 2.5, boxShadow: "0 6px 16px rgba(0,0,0,0.15)" }}
        >
          {toastMsg}
        </Alert>
      </Snackbar>
    </Container>
  );
}
